import { useState, type ReactNode } from 'react'
import { ChevronDown, Download, Eye, Loader2 } from 'lucide-react'
import { reportCard, reportCardPad, reportCardSub, reportCardTitle } from './ui'
import { useReportPreview } from './downloads/useReportPreview'

type Period = 'daily' | 'weekly' | 'monthly'

const DEFAULT_PERIODS: readonly Period[] = ['weekly', 'monthly']

const PERIOD_LABEL: Record<Period, string> = { daily: '1 day', weekly: '7 days', monthly: '30 days' }

/**
 * One downloadable report, through an optional date -- the shape every report on this
 * tab shares. Only what varies (title, endpoint, file extension, which periods it
 * offers, and how to render its preview) is a prop; the period picker, the fetch, the
 * save-as-file dance, and the preview toggle are written once.
 *
 * `periods` defaults to weekly/monthly -- the original two every card shipped with.
 * Pass a wider set (e.g. daily/weekly/monthly) per-card rather than widening this
 * default, since not every report's backend route necessarily supports every period
 * (check the route's own Query pattern before adding 'daily' for a new card).
 *
 * Preview and download share the SAME period/to state on purpose: what you see is what
 * you'd get if you clicked download right now, not a stale snapshot from before you last
 * moved the date picker.
 */
export function ReportDownloadCard<T>({
  icon, iconTone = 'slate', title, description, endpoint, filenamePrefix, extension,
  previewEndpoint, renderPreview, periods = DEFAULT_PERIODS,
}: {
  icon: ReactNode
  iconTone?: 'slate' | 'blue' | 'amber' | 'violet'
  title: string
  description: string
  endpoint: string
  filenamePrefix: string
  extension: 'pdf' | 'xlsx'
  previewEndpoint?: string
  renderPreview?: (data: T) => ReactNode
  periods?: readonly Period[]
}): JSX.Element {
  const [period, setPeriod] = useState<Period>(periods[0] ?? 'weekly')
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(false)

  const canPreview = !!previewEndpoint && !!renderPreview
  const preview = useReportPreview<T>(previewEndpoint ?? '', period, to, showPreview && canPreview)

  async function download(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const q = new URLSearchParams({ period })
      if (to) q.set('to', to)
      const res = await fetch(`${endpoint}?${q}`)
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { error?: string } | null
        throw new Error(body?.error ?? `${res.status} ${res.statusText}`)
      }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const disposition = res.headers.get('content-disposition') ?? ''
      const match = /filename="([^"]+)"/.exec(disposition)
      a.download = match?.[1] ?? `${filenamePrefix}-${period}.${extension}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not generate report')
    } finally {
      setBusy(false)
    }
  }

  const iconBg = {
    slate: 'bg-slate-100', blue: 'bg-blue-50', amber: 'bg-amber-50', violet: 'bg-violet-50',
  }[iconTone]

  return (
    <div className={`${reportCard} overflow-hidden transition-shadow duration-150 hover:shadow-md`}>
      <div className={reportCardPad}>
        <div className="flex items-start gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${iconBg}`}>
            {icon}
          </span>
          <div className="min-w-0 flex-1">
            <div className={reportCardTitle}>{title}</div>
            <p className={`mt-0.5 ${reportCardSub}`}>{description}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
            period
            <div className="flex overflow-hidden rounded-md border border-slate-200">
              {periods.map((p) => (
                <button
                  key={p} type="button" onClick={() => setPeriod(p)}
                  className={`px-3 py-1.5 text-[12px] font-medium capitalize transition-colors ${
                    period === p ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </label>
          <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
            through (optional)
            <input
              type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5 font-mono text-[12px] text-slate-700
                         shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </label>

          <div className="ml-auto flex items-center gap-2">
            {canPreview && (
              <button
                type="button" onClick={() => setShowPreview((v) => !v)}
                className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-[12.5px]
                           font-medium text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:text-slate-900"
              >
                <Eye size={14} />
                Preview
                <ChevronDown size={13} className={`transition-transform duration-150 ${showPreview ? 'rotate-180' : ''}`} />
              </button>
            )}
            <button
              type="button" onClick={() => void download()} disabled={busy}
              className="flex items-center gap-1.5 rounded-md bg-slate-900 px-3.5 py-2 text-[12.5px]
                         font-medium text-white shadow-sm transition-[background-color,transform] duration-100
                         hover:bg-slate-700 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {busy ? 'Generating…' : `Download ${extension.toUpperCase()}`}
            </button>
          </div>
        </div>

        <p className="mt-2 text-[11px] text-slate-400">
          {PERIOD_LABEL[period]} ending {to || 'today'}.
        </p>

        {error && (
          <p className="mt-3 animate-slide-up rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
            Not generated — {error}
          </p>
        )}
      </div>

      {showPreview && canPreview && (
        <div className="animate-slide-up border-t border-slate-100 bg-slate-50/40 p-4">
          {preview.loading ? (
            <div className="flex items-center gap-2 py-6 text-[12px] text-slate-400">
              <Loader2 size={13} className="animate-spin" /> Building preview…
            </div>
          ) : preview.error ? (
            <p className="py-4 text-center text-[12px] text-rose-600">Preview unavailable — {preview.error}</p>
          ) : preview.data ? (
            renderPreview?.(preview.data)
          ) : null}
        </div>
      )}
    </div>
  )
}
